const instances = new Map() // Store instances by selector

export default class Navbar {
    /**
     * Constructor for the Navbar class
     * @param {Object} options - Configuration options for the Navbar
     * @param {string} options.navbarSelector - Selector for the navbar element (default: '#wrapper-navbar')
     * @param {number} options.duration - Animation duration for hiding/showing the navbar (default: 0.5)
     */
    constructor({ navbarSelector = '#wrapper-navbar', duration = 0.5 } = {}) {
        this._el = document.querySelector(navbarSelector)
        this._height = this.el?.offsetHeight || 0
        this._duration = duration

        if (!this.el) {
            throw new Error(`Navbar element not found with selector: ${navbarSelector}`)
        }

        this._navAnimation = null
        this._scrollTrigger = null

        this.init()
    }

    /**
     * Static method to get or create an instance of Navbar
     * @param {Object} options - Configuration options for the Navbar
     * @returns {Navbar} The singleton instance of the Navbar for the given selector
     */
    static getInstance(options = {}) {
        const { navbarSelector = '#wrapper-navbar' } = options

        // Check if an instance already exists for the given selector
        if (!instances.has(navbarSelector)) {
            const instance = new Navbar(options)
            instances.set(navbarSelector, instance)
        }

        // Return the existing instance
        return instances.get(navbarSelector)
    }

    /**
     * Getter for the navbar element
     * @returns {HTMLElement} The navbar element
     */
    get el() {
        return this._el
    }

    /**
     * Getter for the navbar height
     * @returns {number} The height of the navbar
     */
    get height() {
        return this._height
    }

    /**
     * Setter for the navbar height
     * @param {number} height - The new height of the navbar
     */
    set height(height) {
        if (typeof height !== 'number' || height < 0) {
            throw new Error('Navbar height must be a non-negative number')
        }
        this._height = height
    }

    /**
     * Getter for the dynamically calculated navbar height
     * @returns {number} The dynamically calculated height of the navbar
     */
    get heightDynamic() {
        return this._el?.offsetHeight || 0
    }

    /**
     * Getter for the animation duration
     * @returns {number} The animation duration
     */
    get duration() {
        return this._duration
    }

    /**
     * Setter for the animation duration
     * @param {number} duration - The new animation duration
     */
    set duration(duration) {
        if (typeof duration !== 'number' || duration < 0) {
            throw new Error('Animation duration must be a non-negative number')
        }
        this._duration = duration
        if (this._navAnimation) {
            this._navAnimation.duration(this._duration)
        }
    }

    /**
     * Getter for the ScrollTrigger instance
     * @returns {ScrollTrigger} The ScrollTrigger instance
     */
    get scrollTrigger() {
        return this._scrollTrigger
    }

    /**
     * Setter for the ScrollTrigger instance
     * @param {ScrollTrigger} scrollTrigger - The new ScrollTrigger instance
     */
    set scrollTrigger(scrollTrigger) {
        if (!(scrollTrigger instanceof ScrollTrigger)) {
            throw new Error('ScrollTrigger must be an instance of ScrollTrigger')
        }
        this._scrollTrigger = scrollTrigger
    }

    /**
     * Getter for the navbar animation
     * @returns {GSAPAnimation} The GSAP animation instance for the navbar
     */
    get navAnimation() {
        return this._navAnimation
    }

    /**
     * Setter for the navbar animation
     * @param {GSAPAnimation} navAnimation - The new GSAP animation instance for the navbar
     */
    set navAnimation(navAnimation) {
        this._navAnimation = navAnimation
    }

    /**
     * Initialize the Navbar functionality
     */
    init() {
        gsap.registerPlugin(ScrollTrigger)
        this.setupAnimations()
        this.createScrollTrigger()
    }

    /**
     * Setup GSAP animations for the navbar and header
     */
    setupAnimations() {
        // Hide navbar on scroll down
        this.navAnimation = gsap.to(this.navbar, {
            top: -this.height, // when using `y`, offcanvas menu is hidden behind the steps
            duration: this.duration,
            ease: 'power2.in',
            paused: true,
        })
    }

    /**
     * Create the ScrollTrigger instance for the navbar
     */
    createScrollTrigger() {
        this.scrollTrigger = ScrollTrigger.create({
            trigger: this.navbar,
            start: () => `${window.innerHeight * 2 + 1}px top`,
            end: () => Math.max(document.body.offsetHeight, 999999),
            pinSpacing: false,
            invalidateOnRefresh: true,
            onUpdate: ({ direction }) => {
                if (direction === -1) {
                    document.body.classList.remove('navbar-hidden')
                    this.navAnimation?.reverse()
                    this.headerAnimation?.reverse()
                } else if (direction === 1) {
                    document.body.classList.add('navbar-hidden')
                    this.navAnimation?.play()
                    this.headerAnimation?.play()
                }
            },
        })
    }
}
